import { computed, onScopeDispose, ref, watch } from "vue"
import type { AxiosError } from "axios"
import type { StoredReaderPdfBookmark, StoredReaderPdfNote } from "@/interface/DTO_Interfaces"
import { refreshSession, sessionUser } from "@/ts/api/auth-session"
import { readerAnnotationsApi, type ReaderAnnotationScope } from "@/ts/api/reader-annotations"
import { browserStorageGateway } from "@/ts/storage/create-json-storage-gateway"
import { addReaderPdfBookmark, hydrateReaderPdfBookmarks, removeReaderPdfBookmark } from "@/ts/reader-pdf-bookmarks-storage"
import { addReaderPdfNote, hydrateReaderPdfNotes, removeReaderPdfNotes, updateReaderPdfNote } from "@/ts/reader-pdf-notes-storage"

export function useReaderAnnotations(scope: ReaderAnnotationScope) {
        const isWeb = import.meta.env.VITE_EBOOK_TARGET === "web"
        const ownerId = computed(() => isWeb ? sessionUser.value?.id ?? null : "offline")
        const canSave = computed(() => ownerId.value !== null)
        const bookmarks = ref<StoredReaderPdfBookmark[]>([])
        const notes = ref<StoredReaderPdfNote[]>([])
        const error = ref("")
        const isSaving = ref(false)
        const isLoading = ref(false)
        const remote = readerAnnotationsApi(scope)
        let revision = 0

        const reportError = async (reason: unknown, version: number) => {
                if (version !== revision) return
                if ((reason as AxiosError).response?.status === 401) await refreshSession()
                if (version === revision) error.value = reason instanceof Error ? reason.message : "Не удалось обработать заметки и закладки. Попробуйте ещё раз."
        }

        watch(ownerId, async (owner) => {
                const version = ++revision
                bookmarks.value = []
                notes.value = []
                error.value = ""
                isSaving.value = false
                isLoading.value = false
                if (owner === null) return

                if (!isWeb) {
                        bookmarks.value = hydrateReaderPdfBookmarks(browserStorageGateway, scope).items
                        notes.value = hydrateReaderPdfNotes(browserStorageGateway, scope).items
                        return
                }

                isLoading.value = true
                try {
                        const loaded = await remote.read()
                        if (version !== revision) return
                        bookmarks.value = loaded.bookmarks
                        notes.value = loaded.notes
                } catch (reason) {
                        await reportError(reason, version)
                } finally {
                        if (version === revision) isLoading.value = false
                }
        }, { immediate: true, flush: "sync" })

        onScopeDispose(() => { revision += 1 })

        const mutate = async (action: () => Promise<() => void>): Promise<boolean> => {
                if (!canSave.value || isSaving.value || isLoading.value) return false
                const version = revision
                isSaving.value = true
                error.value = ""
                try {
                        const apply = await action()
                        // Ответ прежнего аккаунта не должен менять данные нового владельца.
                        if (version !== revision) return false
                        apply()
                        return true
                } catch (reason) {
                        await reportError(reason, version)
                        return false
                } finally {
                        if (version === revision) isSaving.value = false
                }
        }

        const addBookmark = (bookmark: Omit<StoredReaderPdfBookmark, "id">) => mutate(async () => {
                const saved = isWeb ? await remote.addBookmark(bookmark)
                        : addReaderPdfBookmark(browserStorageGateway, bookmark).bookmarks.items.find(item => item.pageNumber === bookmark.pageNumber)!
                return () => { bookmarks.value = [...bookmarks.value.filter(item => item.pageNumber !== saved.pageNumber), saved] }
        })

        const deleteBookmarks = (pageNumbers: number[]) => mutate(async () => {
                if (isWeb) await remote.removeBookmarks(pageNumbers)
                else for (const pageNumber of pageNumbers) removeReaderPdfBookmark(browserStorageGateway, { ...scope, pageNumber })
                return () => { bookmarks.value = bookmarks.value.filter(item => !pageNumbers.includes(item.pageNumber)) }
        })

        const saveNote = (note: Omit<StoredReaderPdfNote, "id">, noteId?: string) => mutate(async () => {
                let saved: StoredReaderPdfNote
                if (isWeb) saved = noteId ? await remote.updateNote(noteId, { text: note.text, color: note.color }) : await remote.addNote(note)
                else {
                        const result = noteId
                                ? updateReaderPdfNote(browserStorageGateway, { ...scope, noteId, text: note.text, color: note.color, updatedAt: new Date().toISOString() })
                                : addReaderPdfNote(browserStorageGateway, note)
                        saved = noteId ? result.items.find(item => item.id === noteId)! : result.items[result.items.length - 1]
                }
                return () => { notes.value = noteId ? notes.value.map(item => item.id === noteId ? saved : item) : [...notes.value, saved] }
        })

        const deleteNotes = (noteIds: string[]) => mutate(async () => {
                if (isWeb) await remote.removeNotes(noteIds)
                else removeReaderPdfNotes(browserStorageGateway, { ...scope, noteIds })
                return () => { notes.value = notes.value.filter(item => !noteIds.includes(item.id)) }
        })

        return { bookmarks, notes, canSave, ownerId, isSaving, isLoading, error, addBookmark, deleteBookmarks, saveNote, deleteNotes }
}
